import { useState, useCallback } from 'react';
import { useTheme, Button, Grid } from '@simplywallst/ui-core';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';
import DiscoverSolid from '@simplywallst/ui-core/icons/DiscoverSolid';
import {
  Wrapper,
  Label,
  Icon,
  Headline,
  Tiles,
  ScrollPane,
  ChevronRight,
  CallToAction,
} from './styled';
import { useTracking } from 'react-tracking';
import CriteriaDetails from './CriteriaDetails';
import type { PageType } from '@/pages/DiscoverPageV2/constants';

type CriteriaType =
  | 'High Yield'
  | 'Consistent Dividends'
  | 'Dividend Cover'
  | 'Healthy Balance Sheet'
  | 'Profitability'
  | 'Low Cost Producer'
  | 'Growth';

const labels: Record<PageType, CriteriaType[]> = {
  Dividend: [
    'High Yield',
    'Consistent Dividends',
    'Dividend Cover',
    'Healthy Balance Sheet',
  ],
  Gold: ['Healthy Balance Sheet', 'Low Cost Producer', 'Profitability'],
  Growth: ['Growth', 'Healthy Balance Sheet'],
};

const Criteria = ({ pageType }: { pageType: PageType }) => {
  const theme = useTheme();
  const checklist = labels[pageType] || [];
  const { trackEvent } = useTracking();
  const [showCriteria, setShowCriteria] = useState(false);
  const handleShowCriteria = useCallback(() => {
    setShowCriteria((showCriteria) => !showCriteria);
    trackEvent({
      type: 'track',
      subject: 'header criteria',
      modifier: 'learn more',
      action: 'click',
    });
  }, [showCriteria]);

  const criteriaDisplay = showCriteria ? 'grid' : 'inline-grid';

  return (
    <Wrapper
      as="section"
      templateColumns="auto auto"
      justifyContent="start"
      criteriaVisible={showCriteria}
    >
      <Headline
        as="h2"
        column="span 2"
        templateRowsLg={`${theme.x4spacePx}px`}
        columnLg={showCriteria === false ? 'span 2' : '1'}
        alignItemsLg="center"
        templateColumns={`${theme.x4spacePx}px auto`}
      >
        <Icon justifyContent="end">
          <DiscoverSolid />
        </Icon>
        <span>Our criteria to find Top {pageType} Companies</span>
      </Headline>
      <ScrollPane
        criteriaVisible={showCriteria}
        display={criteriaDisplay}
        column={showCriteria ? 'span 2' : undefined}
      >
        <Tiles
          criteriaVisible={showCriteria}
          displayLg={criteriaDisplay}
          templateColumns={
            showCriteria ? `repeat(${checklist.length}, 290px)` : undefined
          }
          templateColumnsMd={`repeat(${checklist.length}, ${
            showCriteria ? '1fr' : 'auto'
          })`}
          gap={`${theme.x2spacePx}px`}
        >
          {checklist.map((n) => (
            <Grid
              as="section"
              key={`checklist-${n}`}
              display={criteriaDisplay}
              templateRows={`${
                showCriteria ? theme.x6spacePx : theme.x5spacePx
              }px auto`}
            >
              <Label
                as="h2"
                templateColumns={`${theme.x4spacePx}px auto`}
                alignItems="center"
                criteriaVisible={showCriteria}
              >
                <TickSolid />
                <span>{n}</span>
              </Label>
              <CriteriaDetails pageType={pageType} criteriaType={n} />
            </Grid>
          ))}
        </Tiles>
      </ScrollPane>
      <CallToAction
        display={criteriaDisplay}
        row="3"
        rowLg={showCriteria ? '1' : '2'}
        column={showCriteria ? 'span 2' : '1'}
        columnLg="2"
        justifyContent={showCriteria ? undefined : 'start'}
        justifyContentLg="end"
        criteriaVisible={showCriteria}
      >
        <Button
          styleType="loud"
          displayMdDown={showCriteria ? 'block' : 'inline'}
          onClick={handleShowCriteria}
          alignIcon={showCriteria === false ? 'right' : undefined}
          data-cy-id="show-criteria"
        >
          {showCriteria ? 'Show less' : 'Learn more'}
          {showCriteria === false && <ChevronRight />}
        </Button>
      </CallToAction>
    </Wrapper>
  );
};

export default Criteria;
